import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date string with a consistent format, preserving the exact time from the input
 * @param timestamp ISO date string to format
 * @returns Formatted date string
 */
export const formatDateTime = (timestamp: string): string => {
  if (!timestamp) return "N/A";
  
  // Parse the date with explicit handling to avoid browser auto-conversion to local timezone
  const [datePart, timePart] = timestamp.split('T');
  if (!datePart || !timePart) return timestamp; // Return original if parsing fails
  
  // Extract time parts - handle various time formats
  let hours, minutes;
  if (timePart.includes(':')) {
    [hours, minutes] = timePart.split(':');
  } else {
    return timestamp; // Return original if time format is unexpected
  }
  
  // Handle cases where hours/minutes couldn't be parsed
  if (hours === undefined || minutes === undefined) return timestamp;
  
  // Parse date parts
  const [year, month, day] = datePart.split('-').map(n => parseInt(n, 10));
  if (!year || !month || !day) return timestamp; // Return original if parsing fails
  
  // Format date parts using locale formatting
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(year, month - 1, 1));
  
  // Convert 24-hour to 12-hour format
  let hour12 = parseInt(hours, 10);
  const ampm = hour12 >= 12 ? 'PM' : 'AM';
  if (hour12 > 12) hour12 -= 12;
  if (hour12 === 0) hour12 = 12;
  
  // Format the time parts manually to avoid any timezone conversion
  return `${monthName} ${day}, ${year}, ${hour12}:${minutes.substring(0, 2)} ${ampm}`;
};

/**
 * Get CSS class for sentiment scores
 */
export const getSentimentColor = (score: number | null | undefined) => {
  if (score === null || score === undefined) return "status-badge-default";
  
  if (score >= 7) return "sentiment-badge sentiment-badge-positive";
  if (score <= 3) return "sentiment-badge sentiment-badge-negative";
  return "sentiment-badge sentiment-badge-neutral";
};

/**
 * Get CSS class for status badges
 */
export const getStatusBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'urgent':
      return "status-badge status-badge-urgent";
    case 'unread':
      return "status-badge status-badge-unread";
    case 'resolved':
    case 'win':
      return "status-badge status-badge-resolved";
    case 'in progress':
      return "status-badge status-badge-inprogress";
    case 'read':
      return "status-badge status-badge-read";
    default:
      return "status-badge status-badge-default";
  }
};

/**
 * Get CSS class for tag badges
 */
export const getTagBadgeClass = (tag: string) => {
  if (tag.toLowerCase().includes('customer shoutout')) {
    return "tag-badge tag-badge-positive";
  }
  return "tag-badge";
}; 